// src/pages/admin/AdminProjects.jsx (FULL FILE) — UPDATED: Projects now support multi-category (categories[] + focus_area primary)

import React, { useEffect, useMemo, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import AdminLayout from "./AdminLayout";
import { adminFetch } from "../../lib/adminAuth";
import { API_URL } from "../../lib/utils";

const initialForm = {
  title: "",
  excerpt: "",
  description: "",
  content: "",
  // ✅ NEW: store multi categories on the form
  categories: ["Humanitarian Relief"],
  // ✅ keep old primary (backend expects it as primary)
  focus_area: "Humanitarian Relief",
  is_active: true,
};

const focusAreas = [
  "Humanitarian Relief",
  "Education",
  "Human Rights",
  "Technology Inclusion",
  "Climate Action",
];

const BACKEND_ORIGIN = API_URL.replace(/\/api\/?$/, "");
const TINYMCE_KEY = process.env.REACT_APP_TINYMCE_API_KEY || "";

const toAbsoluteImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const clean = url.trim();
  if (!clean) return "";
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;
  if (clean.startsWith("/")) return `${BACKEND_ORIGIN}${clean}`;
  return `${BACKEND_ORIGIN}/${clean}`;
};

const fallbackImage =
  "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&auto=format&fit=crop&q=80";

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  const [imageFile, setImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [replaceGallery, setReplaceGallery] = useState(false);

  const [previewUrl, setPreviewUrl] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [existingGallery, setExistingGallery] = useState([]);

  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");

  const loadProjects = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminFetch("/admin/projects");
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const filteredProjects = useMemo(() => {
    if (filter === "active") return projects.filter((p) => p.is_active);
    if (filter === "inactive") return projects.filter((p) => !p.is_active);
    return projects;
  }, [projects, filter]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setImageFile(null);
    setGalleryFiles([]);
    setReplaceGallery(false);
    setExistingImageUrl("");
    setExistingGallery([]);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setError("");
  };

  const safeSetForm = (updater) => {
    setForm((prev) => {
      const base = { ...initialForm, ...(prev || {}) };
      const next = typeof updater === "function" ? updater(base) : updater;

      // ✅ keep focus_area synced to primary category
      const cats = Array.isArray(next.categories) && next.categories.length > 0
        ? next.categories
        : [...initialForm.categories];

      const primary = cats[0] || initialForm.focus_area;

      return {
        ...next,
        categories: cats,
        focus_area: primary,
      };
    });
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;

    // if they ever use the old select, keep it compatible:
    if (name === "focus_area") {
      safeSetForm((prev) => {
        const v = value || initialForm.focus_area;
        // ensure selected focus_area is included in categories and becomes primary
        const current = Array.isArray(prev.categories) ? [...prev.categories] : [];
        const filtered = current.filter((x) => x !== v);
        const nextCats = [v, ...filtered];
        return { ...prev, focus_area: v, categories: nextCats };
      });
      return;
    }

    safeSetForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ multi-category toggle handler
  const toggleCategory = (value) => {
    safeSetForm((prev) => {
      const current = Array.isArray(prev.categories) ? [...prev.categories] : [];
      const exists = current.includes(value);

      let next = exists ? current.filter((c) => c !== value) : [...current, value];

      // never allow empty selection
      if (next.length === 0) next = [initialForm.categories[0]];

      // keep primary as first item
      const primary = next[0] || initialForm.categories[0];
      return { ...prev, categories: next, focus_area: primary };
    });
  };

  const primaryFocusArea = (Array.isArray(form.categories) && form.categories.length > 0)
    ? form.categories[0]
    : initialForm.focus_area;

  const onSelectImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!allowed.includes(file.type)) {
      setError("Only JPG, JPEG, PNG, or WEBP images are allowed.");
      return;
    }

    const max = 8 * 1024 * 1024;
    if (file.size > max) {
      setError("Image size must not exceed 8MB.");
      return;
    }

    setError("");
    setImageFile(file);

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const onSelectGallery = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    const max = 8 * 1024 * 1024;

    for (const f of files) {
      if (!allowed.includes(f.type)) {
        setError("Gallery supports only JPG, JPEG, PNG, WEBP.");
        return;
      }
      if (f.size > max) {
        setError("Each gallery image must be max 8MB.");
        return;
      }
    }

    setError("");
    setGalleryFiles(files);
  };

  const startEdit = (project) => {
    setEditingId(project.id);

    const incomingCats = Array.isArray(project.categories) && project.categories.length > 0
      ? project.categories
      : (project.focus_area ? [project.focus_area] : [...initialForm.categories]);

    // ensure primary is first
    const primary = project.focus_area || incomingCats[0] || initialForm.focus_area;
    const catsOrdered = [primary, ...incomingCats.filter((x) => x !== primary)];

    setForm({
      title: project.title || "",
      excerpt: project.excerpt || "",
      description: project.description || "",
      content: project.content || "",
      categories: catsOrdered,
      focus_area: primary,
      is_active: Boolean(project.is_active),
    });

    setImageFile(null);
    setGalleryFiles([]);
    setReplaceGallery(false);

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setExistingImageUrl(project.image_url || "");
    setExistingGallery(Array.isArray(project.gallery) ? project.gallery : []);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("title", (form.title || "").trim());
    fd.append("excerpt", (form.excerpt || "").trim());
    fd.append("description", (form.description || "").trim());
    fd.append("content", (form.content || "").trim()); // HTML

    // ✅ NEW: send multi categories
    const cats = Array.isArray(form.categories) && form.categories.length > 0
      ? form.categories
      : [...initialForm.categories];

    fd.append("categories_json", JSON.stringify(cats));

    // ✅ keep old required field: primary focus_area
    fd.append("focus_area", (cats[0] || initialForm.focus_area).trim());

    fd.append("is_active", String(!!form.is_active));
    fd.append("replace_gallery", String(!!replaceGallery));

    if (imageFile) fd.append("image", imageFile);
    galleryFiles.forEach((f) => fd.append("gallery_images", f));
    return fd;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const title = (form.title || "").trim();
      const excerpt = (form.excerpt || "").trim();
      const description = (form.description || "").trim();
      const content = (form.content || "").trim();

      const cats = Array.isArray(form.categories) && form.categories.length > 0
        ? form.categories
        : [];

      if (!title || !excerpt || !description || !content) {
        throw new Error("Title, excerpt, description, and content are required.");
      }

      if (!cats.length) {
        throw new Error("Please select at least one category/focus area.");
      }

      if (!editingId && !imageFile) {
        throw new Error("Please upload a featured image.");
      }

      const body = buildFormData();

      if (editingId) {
        await adminFetch(`/admin/projects/${editingId}`, {
          method: "PUT",
          body,
          isMultipart: true,
        });
      } else {
        await adminFetch("/admin/projects", {
          method: "POST",
          body,
          isMultipart: true,
        });
      }

      resetForm();
      await loadProjects();
    } catch (err) {
      setError(err.message || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (projectId) => {
    try {
      await adminFetch(`/admin/projects/${projectId}/toggle-active`, { method: "PATCH" });
      await loadProjects();
    } catch (err) {
      setError(err.message || "Failed to toggle status");
    }
  };

  const deleteProject = async (projectId) => {
    const ok = window.confirm("Delete this project permanently?");
    if (!ok) return;
    try {
      await adminFetch(`/admin/projects/${projectId}`, { method: "DELETE" });
      if (editingId === projectId) resetForm();
      await loadProjects();
    } catch (err) {
      setError(err.message || "Failed to delete project");
    }
  };

  return (
    <AdminLayout title="Manage Projects" onRefresh={loadProjects}>
      <div className="space-y-8">
        {/* Create / Edit Form */}
        <Card className="rounded-2xl border-stone-200 shadow-sm">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-semibold text-stone-900 mb-6">
              {editingId ? "Edit Project" : "Create New Project"}
            </h2>

            {error && (
              <div className="mb-5 rounded-lg bg-red-50 text-red-700 px-4 py-3 text-sm border border-red-100">
                {error}
              </div>
            )}

            <form onSubmit={submitForm} className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Title</label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={onChange}
                    className="w-full rounded-xl border border-stone-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g. Send a Child to School"
                    required
                  />
                </div>

                {/* ✅ MULTI CATEGORIES */}
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <label className="block text-sm font-medium text-stone-700">
                      Categories (select multiple)
                    </label>
                    <span className="text-xs text-stone-500">
                      Primary: <b>{primaryFocusArea || "—"}</b>
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {focusAreas.map((item) => {
                      const checked = Array.isArray(form.categories)
                        ? form.categories.includes(item)
                        : false;

                      return (
                        <button
                          key={item}
                          type="button"
                          onClick={() => toggleCategory(item)}
                          className={`px-3 py-2 rounded-xl border text-sm transition ${
                            checked
                              ? "bg-amber-100 border-amber-300 text-amber-900"
                              : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
                          }`}
                        >
                          <span className="inline-flex items-center gap-2">
                            <span
                              className={`inline-block w-3 h-3 rounded-sm border ${
                                checked
                                  ? "bg-amber-600 border-amber-600"
                                  : "bg-white border-stone-300"
                              }`}
                            />
                            {item}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Hidden legacy primary field */}
                  <input type="hidden" name="focus_area" value={primaryFocusArea} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Excerpt</label>
                <textarea
                  name="excerpt"
                  value={form.excerpt}
                  onChange={onChange}
                  rows={2}
                  className="w-full rounded-xl border border-stone-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Short summary shown on cards..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={onChange}
                  rows={3}
                  className="w-full rounded-xl border border-stone-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-amber-500"
                  placeholder="Brief project description..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Full Content (Rich Text)
                </label>
                <div className="rounded-xl border border-stone-300 overflow-hidden">
                  <Editor
                    apiKey={TINYMCE_KEY}
                    value={form.content}
                    onEditorChange={(value) => safeSetForm((prev) => ({ ...prev, content: value }))}
                    init={{
                      height: 420,
                      menubar: "file edit view insert format tools table help",
                      plugins: [
                        "advlist",
                        "autolink",
                        "lists",
                        "link",
                        "image",
                        "charmap",
                        "preview",
                        "anchor",
                        "searchreplace",
                        "visualblocks",
                        "code",
                        "fullscreen",
                        "insertdatetime",
                        "media",
                        "table",
                        "wordcount",
                        "emoticons",
                      ],
                      toolbar:
                        "undo redo | blocks | bold italic underline strikethrough | " +
                        "alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
                        "link image media table | forecolor backcolor removeformat | code fullscreen preview",
                      block_formats:
                        "Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Blockquote=blockquote",
                      content_style:
                        "body { font-family:Inter,Arial,sans-serif; font-size:16px; line-height:1.75; } h1,h2,h3{line-height:1.3; margin-top:1.2em;} p{margin:0 0 1em 0;}",
                    }}
                  />
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Featured Image {editingId ? "(optional to replace)" : "(required)"}
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={onSelectImage}
                    className="w-full rounded-xl border border-stone-300 px-3 py-2.5 bg-white outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <p className="text-xs text-stone-500 mt-1">JPG, PNG, WEBP • Max 8MB</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Gallery Images (optional)
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={onSelectGallery}
                    className="w-full rounded-xl border border-stone-300 px-3 py-2.5 bg-white outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  {editingId && (
                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-stone-600">
                      <input
                        type="checkbox"
                        checked={replaceGallery}
                        onChange={(e) => setReplaceGallery(e.target.checked)}
                      />
                      Replace existing gallery (instead of appending)
                    </label>
                  )}
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-4 items-start">
                <label className="inline-flex items-center gap-2 text-sm text-stone-700 mt-2">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={!!form.is_active}
                    onChange={onChange}
                  />
                  Active project
                </label>

                <div className="flex flex-wrap justify-start lg:justify-end gap-2">
                  {(previewUrl || existingImageUrl) && (
                    <img
                      src={previewUrl || toAbsoluteImageUrl(existingImageUrl)}
                      alt="Project preview"
                      className="w-24 h-24 rounded-xl object-cover border border-stone-200"
                    />
                  )}
                  {(existingGallery || []).slice(0, 4).map((g, i) => (
                    <img
                      key={i}
                      src={toAbsoluteImageUrl(g)}
                      alt={`Gallery ${i + 1}`}
                      className="w-24 h-24 rounded-xl object-cover border border-stone-200"
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3 flex-wrap pt-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-amber-700 hover:bg-amber-800 rounded-xl"
                >
                  {saving ? "Saving..." : editingId ? "Update Project" : "Create Project"}
                </Button>
                {editingId && (
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">
                    Cancel Edit
                  </Button>
                )}
              </div>

              {/* small debug */}
              <div className="text-xs text-stone-500">
                Selected categories:{" "}
                <span className="font-medium text-stone-700">
                  {(Array.isArray(form.categories) ? form.categories : []).join(", ")}
                </span>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="rounded-2xl border-stone-200">
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-stone-600">
              Total: <b>{projects.length}</b> | Showing: <b>{filteredProjects.length}</b>
            </p>
            <div className="flex gap-2">
              <Button
                variant={filter === "all" ? "default" : "outline"}
                onClick={() => setFilter("all")}
                className="rounded-xl"
              >
                All
              </Button>
              <Button
                variant={filter === "active" ? "default" : "outline"}
                onClick={() => setFilter("active")}
                className="rounded-xl"
              >
                Active
              </Button>
              <Button
                variant={filter === "inactive" ? "default" : "outline"}
                onClick={() => setFilter("inactive")}
                className="rounded-xl"
              >
                Inactive
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Projects List */}
        <div className="grid lg:grid-cols-2 gap-4">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="h-44 bg-stone-200 rounded-2xl animate-pulse" />
            ))
          ) : filteredProjects.length === 0 ? (
            <Card className="rounded-2xl border-stone-200 lg:col-span-2">
              <CardContent className="p-8 text-center text-stone-500">No projects found.</CardContent>
            </Card>
          ) : (
            filteredProjects.map((p) => {
              const cats = Array.isArray(p.categories) && p.categories.length > 0
                ? p.categories
                : (p.focus_area ? [p.focus_area] : []);

              return (
                <Card key={p.id} className="rounded-2xl border-stone-200 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex gap-4 p-4">
                      <img
                        src={toAbsoluteImageUrl(p.image_url) || fallbackImage}
                        alt={p.title}
                        className="w-28 h-28 rounded-xl object-cover border border-stone-200 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-stone-900">{p.title}</h3>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              p.is_active ? "bg-green-100 text-green-700" : "bg-stone-200 text-stone-700"
                            }`}
                          >
                            {p.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>

                        <p className="text-xs text-amber-700 font-medium mb-1">
                          {p.focus_area}
                        </p>

                        {/* ✅ show multi categories */}
                        {cats.length > 1 ? (
                          <p className="text-xs text-stone-500 mb-1">
                            Also: {cats.slice(1).join(", ")}
                          </p>
                        ) : null}

                        <p className="text-xs text-stone-500 mb-2">/{p.slug || p.id}</p>
                        <p className="text-sm text-stone-600 line-clamp-2 mb-1">
                          {p.excerpt || p.description}
                        </p>
                      </div>
                    </div>

                    <div className="px-4 pb-4 flex flex-wrap gap-2">
                      <Button variant="outline" className="rounded-xl" onClick={() => startEdit(p)}>
                        Edit
                      </Button>
                      <Button variant="outline" className="rounded-xl" onClick={() => toggleActive(p.id)}>
                        {p.is_active ? "Set Inactive" : "Set Active"}
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-xl border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => deleteProject(p.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProjects;
